import { Switch, Route, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import PropertyDetail from "@/pages/PropertyDetail";
import Commercial from "@/pages/Commercial";
import Login from "./pages/Auth/Login";
import { store } from '@/Redux/store';
import { Provider, useSelector } from 'react-redux';
import Register from "./pages/Auth/Register";
import Account from "./pages/Account";
import SellerProperty from "./pages/Seller/Property/Index";
import SellerPropertyAdd from "./pages/Seller/Property/Add";
import { Router as WouterRouter } from "wouter";
import Listing from "./pages/Buyer/Listing";
import Favorites from "./pages/Buyer/Favorites";
import CustomLoaderProvider from "./components/Loader";
import Inquires from "./pages/Seller/Inquires";
import Chat from "./pages/Seller/Chat";
import { NotificationProvider, useNotify } from "./notifications/NotificationProvider";
import { useEffect } from "react";
import { initSocket } from "./socket/client";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/Auth/ResetPassword";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminProperty from "./pages/Admin/AdminProperty";
import AdminUsers from "./pages/Admin/AdminUsers";
import SellerPlans from "./pages/SellerPlans";
import SellerPayments from "./pages/SellerPayments";
import AdminSubscriptions from "./pages/Admin/AdminSubscriptions";


function ProtectedRoute({ children, allow = [] }: any) {
  const [location, navigate] = useLocation();
  const { user, token } = useSelector((state: any) => state?.auth);
  const userType = user?.userType;

  // Not logged in -> redirect to /login and remember where to return
  if (!token) {
    navigate(`/login?next=${encodeURIComponent(location)}`, { replace: true });
    return null;
  }

  // Logged in but userType not allowed -> send home
  if (allow.length && !allow.includes(userType)) {
    navigate('/', { replace: true });
    return null;
  }

  return children;
}

function Router() {
  const { isAuthenticated, token, user } = useSelector((state: any) => state?.auth);
  const { notify } = useNotify();

  useEffect(() => {
    const sock = initSocket(token);

    sock.once('connect', () => {
      console.log('✅ Socket connected:', sock?.id);
    });

    sock.on('message:global', (msg: any) => {
      if (msg?.recieverId == user?.id) {
        notify({ title: msg.name, message: msg.text })
      }
    });

    return () => {
      sock.off('message:global');
    };
  }, [token]);

  return (
    <Switch>
      {!isAuthenticated ? (
        <>
          <Route path="/" component={Landing} />
          <Route path="/property/:id" component={PropertyDetail} />
          <Route path="/commercial" component={Commercial} />
          <Route path="/favorites" component={Favorites} />
          <Route path="/rent" component={Commercial} />
          <Route path="/share" component={Commercial} />
          <Route path="/sold" component={Commercial} />
          <Route path="/new-homes" component={Commercial} />
        </>
      ) : (
        <>
          <Route path="/" component={Home} />
          <Route path="/property/:id" component={PropertyDetail} />
          <Route path="/commercial" component={Commercial} />
          <Route path="/favorites" component={Favorites} />
          <Route path="/rent" component={Commercial} />
          <Route path="/share" component={Commercial} />
          <Route path="/sold" component={Commercial} />
          <Route path="/new-homes" component={Commercial} />

          {/* Seller */}
          <Route path="/seller/account" component={Account} />
          <Route path="/seller/property" component={SellerProperty} />
          <Route path="/seller/property/add" component={SellerPropertyAdd} />
          <Route path="/seller/property/edit/:id" component={SellerPropertyAdd} />
          <Route path="/seller/inquires" component={Inquires} />
          <Route path="/seller/chat" component={Chat} />

          {/* Buyer */}
          <Route path="/buyer/account" component={Account} />
          <Route path="/buyer/listings" component={Listing} />
          <Route path="/buyer/favorites" component={Favorites} />
          <Route path="/buyer/chat" component={Chat} />
          <Route path="/buyer/chat/:conversationId" component={Chat} />

          <Route path="/seller/plans" component={() => (
            <ProtectedRoute allow={['seller']}><SellerPlans /></ProtectedRoute>
          )} />
          <Route path="/seller/payments" component={() => (
            <ProtectedRoute allow={['seller']}><SellerPayments /></ProtectedRoute>
          )} />

          {/* Admin Routes */}
          <Route path="/admin/dashboard" component={() => (
            <ProtectedRoute allow={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          )} />
          <Route path="/admin/property" component={() => (
            <ProtectedRoute allow={['admin']}>
              <AdminProperty />
            </ProtectedRoute>
          )} />
          <Route path="/admin/users" component={() => (
            <ProtectedRoute allow={['admin']}>
              <AdminUsers />
            </ProtectedRoute>
          )} />
          <Route path="/admin/subscriptions" component={() => (
            <ProtectedRoute allow={['admin']}>
              <AdminSubscriptions />
            </ProtectedRoute>
          )} />
          <Route path="/admin/account" component={Account} />
          {/* <Route path="/admin/account" component={(Account)} /> */}


        </>
      )}
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/verify-email/:token" component={VerifyEmail} />
      <Route path="/reset-password/:token" component={ResetPassword} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <NotificationProvider>
          <CustomLoaderProvider>
            <TooltipProvider>
              <Toaster />
              <RouterHooked />
            </TooltipProvider>
          </CustomLoaderProvider>
        </NotificationProvider>
      </Provider>
    </QueryClientProvider>
  );
}

// ✅ Hook Wouter to use hash router
function RouterHooked() {
  return (
    <RouterContext>
      <Router />
    </RouterContext>
  );
}
function RouterContext({ children }: { children: React.ReactNode }) {
  return (
    <WouterRouter hook={useHashLocation}>
      {children}
    </WouterRouter>
  );
}

export default App;
